/**
 * Shrink a photo in the browser before it is uploaded.
 *
 * Sharetribe bills image uploads by size (230,000 credits per GB from 1 Dec
 * 2026): a 1.5 MB phone photo costs about 367 credits, the same picture at
 * 2400 px and JPEG quality 0.85 around 100. Nothing on the site shows images
 * larger than that, so the upload is all the visitor ever sees anyway.
 *
 * Returns the original file whenever resizing would not help or is not
 * possible: on the server, for GIF and SVG (animation, vectors), for files
 * already small enough, and if the browser cannot decode the image.
 */

const SKIP_TYPES = ['image/gif', 'image/svg+xml'];
const SMALL_ENOUGH_BYTES = 300 * 1024;

const loadBitmap = async file => {
  if (typeof createImageBitmap === 'function') {
    // Applies EXIF orientation, so phone photos stay upright.
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise(resolve => canvas.toBlob(resolve, type, quality));

export const prepareImageForUpload = async (file, { maxDimension = 2400, quality = 0.85 } = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return file;
  if (!file || !file.type || !file.type.startsWith('image/') || SKIP_TYPES.includes(file.type)) {
    return file;
  }

  try {
    const bitmap = await loadBitmap(file);
    const width = bitmap.width;
    const height = bitmap.height;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    if (scale === 1 && file.size <= SMALL_ENOUGH_BYTES) return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const context = canvas.getContext('2d');
    // PNGs may be transparent: keep them PNG. Everything else becomes JPEG.
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    if (type === 'image/jpeg') {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (typeof bitmap.close === 'function') bitmap.close();

    const blob = await canvasToBlob(canvas, type, quality);
    if (!blob || blob.size >= file.size) return file;

    const name = type === 'image/jpeg' ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : file.name;
    return new File([blob], name, { type, lastModified: Date.now() });
  } catch (e) {
    return file;
  }
};

// Sizes per use. Avatars are shown at most a few hundred pixels wide.
export const IMAGE_UPLOAD_MAX = {
  content: 2400,
  avatar: 800,
};
